import { fromJS } from 'immutable';

import API from 'config/constants/API';
import { SAVE_PROPERTY_SUCCESS, EXPORT_GROUP_PROPERTIES_SUCCESS } from 'data/property';
import { SAVE_LISTINGS_SUCCESS } from 'data/search';
import { getUserLogoUrl } from 'utils/brand';

import * as ActionType from './actions';
import { planTypes } from './constants';
import { inferCardType } from '../../utils/payment';

import { OPEN_LEAD_AUTOMATOR_UPGRADE } from "./actions";

export const defaultPaymentMethod = {
  paymentMethodId: '',
  cardNumber: '',
  cardCode: '',
  maskedCardNumber: '',
  cardName: '',
  cardExpMonth: 1,
  cardExpYear: new Date().getFullYear(),
  billingStreetAddress: '',
  billingStreetAddress2: '',
  billingCity: '',
  billingState: '',
  billingZip: '',
  billingCountry: 'US',
};

const defaultProfile = {
  firstName: '',
  lastName: '',
  phone: '',
  phone2: '',
  username: '',
  password: '',
  businessFirstName: '',
  businessLastName: '',
  businessName: '',
  businessUrl: '',
  businessEmail: '',
  businessPhone: '',
  emailFriendlyFrom: '',
  replyToEmail: '',
  businessStreetAddress: '',
  businessStreetAddress2: '',
  businessCity: '',
  businessState: '',
  businessZip: '',
  logoUrl: '',
  logoHash: Date.now(),
  favoritePropertyAddLimit: 0,
  marketingListAddLimit: 0,
  appendContactRate: 0,
  countySearchEnabled: false,
  newFeatureLabelEnabled: false,
  fullAccount: false,
  overdueAccount: false,
  activeSavedPropertyLimit: 0,
  assignedSavedPropertyLimit: 0,
  periodSavedPropertyQuantity: 0,
  remainingSavedProperties: 0,
  activeExportPropertyLimit: 0,
  assignedExportPropertyLimit: 0,
  periodExportPropertyQuantity: 0,
  remainingExportProperties: 0,
  nextCycleDate: null,
  mrcTotal: 0,
  csPhone: '',
  csEmail: '',
  pabEnabled: false,
  investorOutreachEnabled: false,
  voicemailUrl: null,
  postcardUrl: null,
  contactAppendUrl: null,
  paymentMethods: [],
  catalogProducts: [],
  planMrcs: [],
  listManagementEnabled: false,
  subscriptionPlanMrcId: null,
  listManagementPlanMrcId: null,
  remainingPrepaidCredit: 0,
  ...defaultPaymentMethod,
};
const defaultLeadAutomator = {
  userId: '',
  planMrcs: [],
  accountPlanMrcs: [],
  paymentMethods: [],
  monitoredProperties: 0,
  monitoredPropertiesUsed: 0,
  monitoredPropertiesRemaining: 0,
  percentOfUsedProperties: ""
};


export const billingSection = {
  dashboard: {},
  wallet: [],
  cardList: [],
  defaultPaymentSettingList: [],
  billingHistory: [],
  isLoading: false,
  error: null,
};

const defaultState = fromJS({
  duplicateSession: false,    // Whether the user is already logged in in a different session. When true they're presented with the option of logging out the other person.
  loggedOut: false,           // Whether the user has been logged out, either manually or automatically.
  logoutMessage: null,        // Logout message to be displayed to user. When null they are simply redirected to the Login screen.
  isLoading: false,
  loading: false,             // Separate "loading" variable; the "isLoading" variable triggers a full screen loading spin wheel, and this one is intended for specific element loading status. (E.g. just buttons instead of whole window.)
  isAuthed: false,            // User has been authenticated
  alertLoading: false,
  getAlertLoading: false,
  offerLoading: false,
  profile: defaultProfile,
  primaryUserId: null,
  customerNumber: null,
  reports: [],
  permissions: [],
  offers: [],
  pages: [],
  alerts: [],
  layouts: [],
  users: null,
  transactions: null,
  chatVisible: false,
  initialPath: null,          // Initial path / route to load.
  marketingProfile: {},
  prepaidCredits: null,
  userProfile: {},
  leadAutomator: defaultLeadAutomator,
  isLeadAutomatorLoaded: false,
  batchDialerSource: null,
  alert: null,
  teams: {
    teamMembers: [],
    selectedTeamMember: {},
    teamCounts: { fullAccessCount: 0, scoutUserCount: 0 },
    canCreate: false,
    isPaidUser: false,
    plantypes: [],
    vendorPhoneNumber: '',
    error: null,
    loading: false,
    vendorEmailAddress: ''
  },
  plan: "Pro",
  billing: billingSection,
  usage: {
    savesRemaining: 0,
    exportsRemaining: 0,
    spendRemaining: 0,
    resetsOnDate: null,
    savesLimit: 0,
    exportsLimit: 0,
    spendLimit: null,
    savesMinQuantity: 0,
    savesCostEach: 0,
    exportsMinQuantity: 0,
    exportsCostEach: 0,
    resetsInDays: 0,
    savesUsedPercent: 0,
    exportsUsedPercent: 0,
    spendUsedPercent: 0,
    saveCatalogProductId: 0,
    exportCatalogProductId: 0,
    id: null
  },
  integrations: {
    batchDialer: [],
    campaigns: [],
    stateAreaCodes: [],
    callSettings: [],
    callSettingsLoading: false,
    callSettingsLoaded: false,
    loadingAvailableOutboundNumbers: false,
    isLeadAutomatorUpgradeOpen: false,
    callCountForToday: 0,
    callCountForTodayLoading: false,
  },
  callCompliance: {
    calldnc: false,
    calllitigator: false,
    calldnclitigator: false
  },
});

const getProfile = profile => ({
  ...defaultProfile,
  ...profile,
  logoUrl: `${API.BASE_URL}${profile.logoUrl}`,
  cardNumberMasked: profile.cardNumber,
  cardNumber: '',
  planMrcs: profile.accountPlanMrcs || [],
});

function saveProfile(state) {
  return state.merge({
    isLoading: true,
  });
}
// reducer function
export default function reducer(state = defaultState, action) {
  const { response } = action;

  switch (action.type) {
    case ActionType.AUTHENTICATE:
      return state.set('isLoading', true);

    case ActionType.AUTHENTICATION_SUCCESS:
    case ActionType.SAVE_PRELIMINARY_DATA_SUCCESS: {
      const { duplicateSession, profile, reports, alerts, layouts = [], permissions, pages, analysisFields, teamMembers = [], authToken, cardCodeRequired, redirectMessage, initialPath, primaryUserId, customerNumber } = response;

      if (authToken) window.appAuthToken = authToken;

      return state.mergeDeep({
        duplicateSession: duplicateSession,
        isLoading: false,
        loading: false,
        isAuthed: true,
        profile: getProfile(profile),
        reports: reports,
        alerts: alerts,
        layouts: layouts || [],
        permissions: permissions,
        pages: pages,
        analysisFields: analysisFields,
        cardCodeRequired,
        redirectMessage,
        initialPath,
        users: (teamMembers || []).map(u => ({ ...u, logoUrl: getUserLogoUrl(u.id) })),
        primaryUserId: primaryUserId,
        customerNumber: customerNumber,
      });
    }

    case ActionType.AUTHENTICATION_ERROR:
      return state.set('isLoading', false);

    case ActionType.SET_LOGOUT:
      return state.merge({ loggedOut: true, logoutMessage: action.message });

    case ActionType.SET_CHAT_VISIBLE:
      return state.merge({ chatVisible: action.visible });

    case ActionType.UPGRADE_ACCOUNT:
    case ActionType.GET_USERS:
    case ActionType.SAVE_USER:
    case ActionType.DELETE_USER:
    case ActionType.GET_TRANSACTIONS:
    case ActionType.SAVE_LAYOUT:
    case ActionType.DELETE_LAYOUT:
    case ActionType.SAVE_TAG:
    case ActionType.DELETE_TAG:
    case ActionType.SAVE_PRELIMINARY_DATA:
    case ActionType.SAVE_PREPAID_CREDIT:
      return state.set('loading', true);

    // ID23-133, update remainingPrepaidCredit value and return the new state
    case ActionType.GET_PREPAID_CREDIT_SUCCESS:
      let remainingPrepaidCredit = action.response
      if (!remainingPrepaidCredit) {
        remainingPrepaidCredit = 0;
      }
      return state.set('loading', false).setIn(['profile', 'remainingPrepaidCredit'], remainingPrepaidCredit);

    case ActionType.UPGRADE_ACCOUNT_ERROR:
    case ActionType.GET_USERS_ERROR:
    case ActionType.SAVE_USER_ERROR:
    case ActionType.DELETE_USER_ERROR:
    case ActionType.GET_TRANSACTIONS_ERROR:
    case ActionType.SAVE_LAYOUT_ERROR:
    case ActionType.DELETE_LAYOUT_ERROR:
    case ActionType.SAVE_TAG_ERROR:
    case ActionType.DELETE_TAG_ERROR:
    case ActionType.SAVE_PRELIMINARY_DATA_ERROR:
    case ActionType.SAVE_PREPAID_CREDIT_ERROR:
      return state.set('loading', false);

    case ActionType.SAVE_TAG_SUCCESS:
    case ActionType.DELETE_TAG_SUCCESS:
      return state.set('loading', false);

    case ActionType.GET_USERS_SUCCESS:
    case ActionType.SAVE_USER_SUCCESS:
    case ActionType.DELETE_USER_SUCCESS:
      return state.merge({ loading: false, users: (response || []).map(u => ({ ...u, logoUrl: getUserLogoUrl(u.id) })) });

    case ActionType.GET_TRANSACTIONS_SUCCESS:
      return state.merge({ loading: false, transactions: response || [] });

    case ActionType.SAVE_LAYOUT_SUCCESS:
    case ActionType.DELETE_LAYOUT_SUCCESS:
      return state.merge({ loading: false, layouts: response.layouts });

    case ActionType.LOAD_ALERT:
      return state.set('getAlertLoading', true);

    case ActionType.LOAD_ALERT_SUCCESS:
      return state.merge({ getAlertLoading: false, alert: fromJS(action.response) });

    case ActionType.LOAD_ALERT_ERROR:
      return state.set('getAlertLoading', false);

    case ActionType.DISABLE_ALERT:
      return state.set('alertLoading', true);

    case ActionType.DISABLE_ALERT_SUCCESS:
    case ActionType.DISABLE_ALERT_ERROR:
      return state.set('alertLoading', false);

    case ActionType.SAVE_PROFILE:
      return saveProfile(state);

    case ActionType.SAVE_PROFILE_SUCCESS:
      return state.merge({
        isLoading: false,
        profile: getProfile(response),
      });

    case ActionType.LOAD_WALLET_HISTORY_LIST_SUCCESS:
      return state
        .set('loading', false)
        .setIn(['billing', 'billingHistory'], fromJS(action.payload));

    case ActionType.LOAD_WALLET_HISTORY_LIST_ERROR:
      return state
        .set('loading', false)
        .setIn(['billing', 'billingHistory'], fromJS([]));

    case ActionType.SAVE_PREPAID_CREDIT_SUCCESS:
      return state.set('loading', false).setIn(['profile', 'remainingPrepaidCredit'], response.remainingPrepaidCredit);

    case ActionType.SAVE_PROFILE_ERROR:
      return state.merge({
        isLoading: false,
      });

    case ActionType.REMOVE_ERROR:
      return state.merge({
        profile: {
          success: null,
          message: null,
        },
      });

    case ActionType.UPGRADE_ACCOUNT_SUCCESS:
    case EXPORT_GROUP_PROPERTIES_SUCCESS:
    case SAVE_PROPERTY_SUCCESS:
    case SAVE_LISTINGS_SUCCESS: {
      const { accountPlanMrcs } = response;
      const fields = 'fullAccount,overdueAccount,nextCycleDate,mrcTotal,paymentMethods,listManagementEnabled,subscriptionPlanMrcId,listManagementPlanMrcId,accountPlanMrcs,activeSavedPropertyLimit,assignedSavedPropertyLimit,periodSavedPropertyQuantity,remainingSavedProperties,activeExportPropertyLimit,assignedExportPropertyLimit,periodExportPropertyQuantity,remainingExportProperties,planMrcs'.split(',');
      const profile = fields.reduce((r, f) => ({ ...r, ...(response[f] === undefined ? {} : { [f]: response[f] }) }), {});

      if (accountPlanMrcs) profile.planMrcs = accountPlanMrcs;

      return state
        .set('loading', false)
        .mergeIn(['profile'], profile);
    }

    case ActionType.LOAD_OFFERS:
    case ActionType.SAVE_OFFER:
    case ActionType.DELETE_OFFER:
    case ActionType.LOAD_OFFER_REPORT:
      return state.set('offerLoading', true);

    case ActionType.LOAD_OFFERS_ERROR:
    case ActionType.SAVE_OFFER_ERROR:
    case ActionType.DELETE_OFFER_ERROR:
    case ActionType.LOAD_OFFER_REPORT_ERROR:
      return state.set('offerLoading', false);

    case ActionType.LOAD_OFFERS_SUCCESS:
    case ActionType.SAVE_OFFER_SUCCESS:
    case ActionType.DELETE_OFFER_SUCCESS:
      return state.set('offerLoading', false).merge({ offers: response || [] });

    case ActionType.LOAD_OFFER_REPORT_SUCCESS:
      return state;

    // marketing profile
    case ActionType.LOAD_MARKETING_PROFILE_SUCCESS:
      return state.set('loading', false).set('marketingProfile', fromJS(action.payload));

    case ActionType.LOAD_USER_PROFILE_SUCCESS:
      return state.set('loading', false).set('userProfile', fromJS(action.payload));

    case ActionType.LOAD_USER_PROFILE_ERROR:
      return state.set('loading', false).set('userProfile', null);

    case ActionType.LOAD_PREPAID_CREDITS_SUCCESS:
      return state.set('loading', false).set('prepaidCredits', fromJS(action.payload));

    case ActionType.LOAD_PLAN_SUCCESS:
      return state.set('loading', false).set('planDetails', fromJS(action.payload));

    case ActionType.LOAD_LA_PLAN_SUCCESS:
      return state.set('loading', false).set('laPlanDetails', fromJS(action.payload));
    case ActionType.LOAD_USAGE_SUCCESS:
      return state.set('loading', false).set('usage', fromJS(action.payload));



    case ActionType.LOAD_LEAD_AUTOMATOR_SUCCESS:
      return state.set('loading', false)
        .set('isLeadAutomatorLoading', false)
        .set('isLeadAutomatorLoaded', true)
        .set('leadAutomator', fromJS(action.payload));

    case ActionType.IS_LEAD_AUTOMATOR_LOADING:
      return state.set('loading', false).set('isLeadAutomatorLoading', fromJS(action.payload));

    case ActionType.UPDATE_SELECTED_LEAD_AUTOMATOR:
      return state.set('loading', false).set('leadAutomator', state.get('leadAutomator').merge({
        selectedLead: fromJS(action.payload)
      }));
    case ActionType.UPDATE_TRIAL_14_LEAD_AUTOMATOR:
      return state.set('loading', false).set('leadAutomator', state.get('leadAutomator').merge({
        trial14LA: action.payload,
      }));
    case ActionType.UPDATE_PAYMENT_PROFILE: {
      const newProfile = { ...state.get('profile').toJS(), ...action.payload };
      return state
        .set('loading', false)
        .set('profile', fromJS(newProfile));
    }
    case ActionType.LOAD_TEAM_MEMBERS_REQUEST:
      return state
        .setIn(["teams", "loading"], true)
        .setIn(["teams", "error"], null);

    case ActionType.LOAD_TEAM_MEMBERS_ERROR:
      return state
        .setIn(["teams", "loading"], false)
        .setIn(["teams", "error"], action.error);
    case ActionType.LOAD_TEAM_MEMBERS_SUCCESS:
      const teamMembers = fromJS(action.payload || []);

      // Compute teamCounts
      const teamCounts = teamMembers.reduce(
        (acc, member) => {
          if (member?.get('userType') === 'SCOUT') {
            acc.scoutUserCount += 1;
          } else {
            acc.fullAccessCount += 1;
          }
          return acc;
        },
        { fullAccessCount: 0, scoutUserCount: 0 }
      );

      // Get user plan from state
      const userPlan = state.get("plan") || 'pro';
      // Find the corresponding plan
      const plan = planTypes.find((p) => p.id === userPlan);
      const canCreate = teamCounts.fullAccessCount < plan?.maxfull;
      const isPaidUser = teamCounts.fullAccessCount >= plan?.preLoaded && teamCounts.fullAccessCount < plan?.maxfull

      return state
        .set("loading", false)
        .setIn(["teams", "teamMembers"], fromJS(teamMembers))
        .setIn(["teams", "loading"], false)
        .setIn(["teams", "teamCounts"], fromJS(teamCounts))
        .setIn(["teams", "error"], null)
        .setIn(["teams", "canCreate"], fromJS(canCreate))
        .setIn(["teams", "isPaidUser"], fromJS(isPaidUser));

    case ActionType.SET_SELECTED_TEAM_MEMBER:
      return state.set('loading', false).setIn(['teams', 'selectedTeamMember'], fromJS(action.payload));

    // batch dialer integration
    case ActionType.LOAD_BATCH_DIALER_INTEGRATION_SUCCESS:
      console.log("REDUCER: batch dialer response →", action.payload);  //
      return state
        .set('loading', false)
        .setIn(['integrations', 'batchDialer'], fromJS(action.payload));

    case ActionType.LOAD_BATCH_DIALER_INTEGRATION_ERROR:
      return state
        .set('loading', false)
        .setIn(['integrations', 'batchDialer'], fromJS([]));

    // campaigns integration
    case ActionType.LOAD_CAMPAIGNS_INTEGRATION_SUCCESS:
      return state
        .set("loading", false)
        .setIn(["integrations", "campaigns"], fromJS(action.payload));

    case ActionType.LOAD_CALL_SETTING_INTEGRATION:
      return state
        .setIn(['integrations', 'callSettingsLoading'], true);


    case ActionType.LOAD_CALL_SETTING_INTEGRATION_SUCCESS:
      console.log("REDUCER: call settings response →", action.payload);
      return state
        .setIn(['integrations', 'callSettingsLoading'], false)
        .setIn(['integrations', 'callSettingsLoaded'], true)
        .setIn(['integrations', 'callSettings'], fromJS(action.payload.callSettings));

    case ActionType.LOAD_CALL_SETTING_INTEGRATION_ERROR:
      // failed refresh says nothing about the account's real settings — keep the last known good list
      return state
        .setIn(['integrations', 'callSettingsLoading'], false);

    case ActionType.LOAD_STATE_AREA_INTEGRATION_SUCCESS:
      return state
        .set('loading', false)
        .setIn(
          ['integrations', 'stateAreaCodes'],
          fromJS(action.payload)
        );

    case ActionType.LOAD_STATE_AREA_INTEGRATION_ERROR:
      return state
        .set('loading', false)
        .setIn(['integrations', 'stateAreaCodes'], fromJS([]));


    case ActionType.DELETE_CALL_SETTING_SUCCESS:
      return state
        .set('loading', false);

    case ActionType.DELETE_CALL_SETTING_ERROR:
      return state
        .set('loading', false);

    case ActionType.LOAD_CALL_SETTINGS_STATE_AREA_CODES_SUCCESS:
      console.log("REDUCER: call settings - state area codes response →", action.payload);
      return state
        .set('loading', false)
        .setIn(['integrations', 'stateAreaCodes'], fromJS(action.payload));

    case ActionType.LOAD_CALL_SETTINGS_STATE_AREA_CODES_ERROR:
      return state
        .set('loading', false)
        .setIn(['integrations', 'stateAreaCodes'], fromJS([]));

    case ActionType.LOADING_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS:
      return state
        .setIn(['integrations', 'loadingAvailableOutboundNumbers'], true);

    case ActionType.LOAD_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS_SUCCESS:
      console.log("REDUCER: call settings - available outbound numbers response →", action.payload);
      return state
        .setIn(['integrations', 'loadingAvailableOutboundNumbers'], false)
        .setIn(['integrations', 'availableOutboundNumbers'], fromJS(action.payload.numbers));

    case ActionType.LOAD_CALL_SETTINGS_AVAILABLE_OUTBOUND_NUMBERS_ERROR:
      return state
        .setIn(['integrations', 'loadingAvailableOutboundNumbers'], false)
        .setIn(['integrations', 'availableOutboundNumbers'], null);

    case ActionType.LOAD_CAMPAIGNS_INTEGRATION_ERROR:
      return state
        .set("loading", false)
        .setIn(["integrations", "campaigns"], fromJS([]));

    // Click to Dial integration

    case ActionType.LOAD_CALL_COUNT_FOR_TODAY:
      return state
        .setIn(['integrations', 'callCountForTodayLoading'], true);

    case ActionType.LOAD_CALL_COUNT_FOR_TODAY_SUCCESS:
      return state
        .setIn(['integrations', 'callCountForTodayLoading'], false)
        .setIn(
          ['integrations', 'callCountForToday'],
          action.payload?.callCountForToday || 0
        );

    case ActionType.LOAD_CALL_COUNT_FOR_TODAY_ERROR:
      return state
        .setIn(['integrations', 'callCountForTodayLoading'], false)
        .setIn(['integrations', 'callCountForToday'], 0);

    case ActionType.OPEN_CLICK_TO_DIAL: {
      const opened = state
        .setIn(['integrations', 'isClickToDialOpen'], true)
        .updateIn(
          ['integrations', 'clickToDialData'],
          (prev) => action.payload ?? prev
        );
      // PROPS-3840: a newly selected contact must never see the previous contact's details
      // (compliance flags, name, softphone contact); they are re-fetched for the new contact.
      if (!action.payload) return opened;
      return opened
        .setIn(['integrations', 'contactPropertyDetails'], null)
        .setIn(['integrations', 'contactPropertyDetailsLoadFailed'], false);
    }

    case ActionType.CLOSE_CLICK_TO_DIAL:
      return state
        .setIn(['integrations', 'isClickToDialOpen'], false)
        .setIn(['integrations', 'clickToDialData'], null)
        .setIn(['integrations', 'contactPropertyDetails'], null)
        .setIn(['integrations', 'contactPropertyDetailsLoadFailed'], false);

    case ActionType.IS_CLICK_TO_DIAL_LOADED:
      return state
        .setIn(['integrations', 'isClickToDialLoaded'], action.payload);

    case ActionType.LOAD_CLICK_TO_DIAL_CONTACT_PROPERTY_DETAILS_SUCCESS: {
      // PROPS-3840: drop a late response for a contact that is no longer selected
      const selectedContactId = state.getIn(['integrations', 'clickToDialData'])?.contactId;
      if (selectedContactId == null || selectedContactId != action.contactId) {
        return state.set('loading', false);
      }
      // Details that aren't for the requested contact can't be trusted -- a failed load, not an endless spinner
      if (action.response?.contactInfo?.contactId != action.contactId) {
        return state
          .set('loading', false)
          .setIn(['integrations', 'contactPropertyDetails'], null)
          .setIn(['integrations', 'contactPropertyDetailsLoadFailed'], true);
      }
      return state
        .set('loading', false)
        .setIn(['integrations', 'contactPropertyDetails'], fromJS(action.response));
    }

    case ActionType.LOAD_CLICK_TO_DIAL_CONTACT_PROPERTY_DETAILS_ERROR:
      // PROPS-3840: a late error for a contact that is no longer selected must not fail the current one
      if (state.getIn(['integrations', 'clickToDialData'])?.contactId != action.contactId) {
        return state.set('loading', false);
      }
      return state
        .set('loading', false)
        .setIn(['integrations', 'contactPropertyDetails'], null)
        .setIn(['integrations', 'contactPropertyDetailsLoadFailed'], true);

    //UPGRADE_TO_LEAD_AUTOMATOR

    case OPEN_LEAD_AUTOMATOR_UPGRADE:
      return state.setIn(
        ['integrations', 'isLeadAutomatorUpgradeOpen'],
        action.payload
      );

    // billing
    case ActionType.LOAD_BILLING_DASHBOARD_SUCCESS:
      return state.set('loading', false).setIn(['billing', 'dashboard'], fromJS(action.payload));

    case ActionType.LOAD_ACCOUNT_TYPE_SUCCESS:
      return state.set('loading', false).setIn(['plan'], fromJS(action.payload));

    case ActionType.LOAD_PAYMENT_LIST_SUCCESS:
      const walletlist = action.payload || [];
      const cardList = walletlist?.map((data) => {
        return {
          label: data?.cardName,
          cardEnding: data?.cardNumber?.slice(-4),
          expDate: data?.cardExp,
          paymentMethodId: data?.paymentMethodId,
          cardTypeDetails: inferCardType(data?.cardNumber || ''),
          cardCVVLength: data?.cardType === "AMEX" ? 4 : 3,
          defaultPaymentMethods: data?.defaultPaymentMethods
        };
      })

      return state
        .set("loading", false)
        .setIn(["billing", "wallet"], fromJS(walletlist))
        .setIn(["billing", "cardList"], fromJS(cardList))


    case ActionType.LOAD_DEFAULT_PAYMENT_SETTING_SUCCESS:
      return state.set('loading', false).setIn(['billing', 'defaultPaymentSettingList'], fromJS(action.payload));

    case ActionType.LOAD_PLAN_TYPE_SUCCESS:
      return state.set('loading', false).setIn(['teams', 'plantypes'], fromJS(action.payload?.planMrcs || []))
        .setIn(['teams', 'vendorPhoneNumber'], fromJS(action.payload?.vendorPhoneNumber || ''))
        .setIn(['teams', 'vendorEmailAddress'], fromJS(action.payload?.vendorEmailAddress || ''))
        .setIn(['teams', 'tmNextBillingCycleDate'], fromJS(action.payload?.tmNextBillingCycleDate || ''))
        .setIn(['teams', 'remainingCycleDays'], fromJS(action.payload?.remainingCycleDays || 0));

    case ActionType.UPDATE_SELECTED_MANAGE_PLAN:
      return state.set('loading', false).set('planDetails', state.get('planDetails').merge({
        selectedManagePlan: fromJS(action.payload)
      }));
    case ActionType.UPDATE_SELECTED_CHOOSE_TIER_TOOLTIP:
      return state.set('loading', false).set('planDetails', state.get('planDetails').merge({
        selectedChooseTierTooltip: fromJS(action.payload)
      }));
    case ActionType.UPDATE_PLAN_MONTHLY_ANNUAL:
      return state.set('loading', false).set('planDetails', state.get('planDetails').merge({
        isAnnualPlanSelected: fromJS(action.payload)
      }));

    case ActionType.LOAD_PAYMENT_LIST:
    case ActionType.ADD_PAYMENT_METHOD:
    case ActionType.UPDATE_PAYMENT_METHOD:
    case ActionType.DELETE_PAYMENT_METHOD:
      return state.set('isLoading', true);


    case ActionType.LOAD_BILLING_DASHBOARD_ERROR:
    case ActionType.LOAD_PAYMENT_LIST_ERROR:
    case ActionType.ADD_PAYMENT_METHOD_ERROR:
    case ActionType.UPDATE_PAYMENT_METHOD_ERROR:
    case ActionType.DELETE_PAYMENT_METHOD_ERROR:
    case ActionType.LOAD_DEFAULT_PAYMENT_SETTING_ERROR:
      return state.set('isLoading', false);
    case ActionType.SET_BATCH_DIALER_SOURCE:
      return state.set('batchDialerSource', action.payload);


    default:
      return state;
  }
}
